import React, { useEffect } from "react";
import {
  FormControl,
  InputLabel,
  TextField,
  Autocomplete,
  Grid,
  GridProps,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { IFilterDropdown } from "../../../AppState/overagesFilterFormActions";
import AppState from "../../../AppState";
import { useSelector } from "react-redux";
import { useCommodityMaster } from "../../../@hooks/useCommodityMaster";
import { FORM_STYLES, S_GRID_PROPS } from "../../../constants";

export interface CommodityFieldProps {
  id: string;
  label: string;
  value?: string;
  onChange?: (
    event: React.SyntheticEvent,
    value: IFilterDropdown | null,
  ) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  options?: IFilterDropdown[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  gridProps?: GridProps;
  loading?: boolean;
  noOptionsText?: string;
  error?: boolean;
  helperText?: string;
}

const CommodityField: React.FC<CommodityFieldProps> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  required = false,
  disabled = false,
  gridProps = S_GRID_PROPS,
  loading = false,
  noOptionsText = "Records not found",
  helperText,
}) => {
  const getOptionLabel = (option: IFilterDropdown | string | null) => {
    if (!option) return "";
    if (typeof option === "string") return option;
    return `${option.displayValue} - ${option.code}`;
  };

  const commodityOptions = useSelector(
    (state: AppState) => state.commodityMasterData,
  );

  const { loadCommodityOptions } = useCommodityMaster();

  useEffect(() => {
    if (!commodityOptions?.length) {
      loadCommodityOptions();
    }
  }, [commodityOptions, loadCommodityOptions]);

  return (
    <Grid item {...gridProps}>
      <FormControl fullWidth size="small">
        <InputLabel style={FORM_STYLES.LABEL} shrink={true} id={`${id}Label`}>
          {label}
          {required ? <sup>*</sup> : <sup>&nbsp;</sup>}
        </InputLabel>
        <Autocomplete
          disablePortal
          disabled={disabled}
          loading={loading}
          className=" custom-autocomplete"
          id={id}
          size="small"
          disableClearable={true}
          value={
            commodityOptions.find((option) => {
              if (option.code == null || value == null) return false;
              // Pad both codes to the same length for comparison
              return option.code === value;
            }) ?? undefined
          }
          options={commodityOptions}
          getOptionLabel={getOptionLabel}
          onChange={onChange}
          onBlur={onBlur}
          noOptionsText={noOptionsText}
          style={FORM_STYLES.LABEL}
          forcePopupIcon={false}
          popupIcon={
            <div className="dropdown-icon-group">
              <ArrowDropUpIcon className="icon-style" />
              <ArrowDropDownIcon className="icon-style" />
            </div>
          }
          renderInput={(params) => (
            <TextField
              autoComplete="off"
              {...params}
              placeholder={placeholder}
              variant="outlined"
              className="dropdown-list"
              name={id}
              // Show selected option as title (tooltip)
              title={getOptionLabel(
                commodityOptions.find((option) => option.code === value) ?? value ?? ""
              )}
              style={FORM_STYLES.LABEL}
              fullWidth
              error={!!helperText}
              helperText={helperText}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "0.9rem" },
              }}
            />
          )}
          renderTags={(value) => (
            <div
              className="auto-complete-selected-container"
              title={value
                .map((item) => `${item.displayValue} - ${item.code}`)
                .join(", ")}
            >
              {value
                .map((item) => `${item.displayValue} - ${item.code}`)
                .join(", ")}
            </div>
          )}
          renderOption={(props, option) => (
            <li {...props} title={getOptionLabel(option)}>
              {getOptionLabel(option)}
            </li>
          )}
        />
      </FormControl>
    </Grid>
  );
};

export default CommodityField;
